import { useState, useEffect, useRef } from 'react';
import { getDashboardHeatmap } from '../api';

function scoreToStyle(score) {
  if (score === null) {
    return { background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0' };
  }
  if (score > 0.2) {
    const op = Math.min(1, (score - 0.2) / 0.8 * 0.8 + 0.2);
    const lightOp = op * 0.35 + 0.08;
    return {
      background: `rgba(22,163,74,${lightOp})`,
      color: score > 0.6 ? '#14532d' : '#166534',
      border: `1px solid rgba(22,163,74,${lightOp + 0.15})`,
    };
  }
  if (score < -0.2) {
    const op = Math.min(1, (Math.abs(score) - 0.2) / 0.8 * 0.8 + 0.2);
    const lightOp = op * 0.35 + 0.08;
    return {
      background: `rgba(220,38,38,${lightOp})`,
      color: Math.abs(score) > 0.6 ? '#7f1d1d' : '#991b1b',
      border: `1px solid rgba(220,38,38,${lightOp + 0.15})`,
    };
  }
  return { background: '#e2e8f0', color: '#64748b', border: '1px solid #cbd5e1' };
}

function buildDays(n) {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (n - 1 - i));
    return d.toISOString().substring(0, 10);
  });
}

function sentimentLabel(score) {
  if (score === null) return { label: 'No Data', color: '#94a3b8', icon: '—' };
  if (score > 0.6)  return { label: 'Strongly Bullish', color: '#16a34a', icon: '▲▲' };
  if (score > 0.2)  return { label: 'Bullish',          color: '#22c55e', icon: '▲' };
  if (score < -0.6) return { label: 'Strongly Bearish', color: '#dc2626', icon: '▼▼' };
  if (score < -0.2) return { label: 'Bearish',          color: '#ef4444', icon: '▼' };
  return { label: 'Neutral', color: '#94a3b8', icon: '●' };
}

function ScoreBar({ score }) {
  if (score === null) return null;
  const pct = Math.round(Math.abs(score) * 100);
  const isPos = score >= 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
      <div style={{
        flex: 1, height: 5, borderRadius: 3,
        background: 'rgba(148,163,184,0.2)',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: isPos ? 'flex-start' : 'flex-end',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          borderRadius: 3,
          background: isPos ? '#22c55e' : '#ef4444',
          transition: 'width 0.3s ease',
        }} />
      </div>
      <span style={{ fontSize: 10, color: '#94a3b8', minWidth: 28, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

function HoverTooltip({ info }) {
  if (!info) return null;
  const { symbol, day, dp } = info;
  const score = dp?.avg_score ?? null;
  const { label, color, icon } = sentimentLabel(score);
  const formattedDay = day
    ? new Date(day + 'T12:00:00Z').toLocaleDateString([], {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      })
    : 'Last 7 Days (Aggregate)';

  const bg   = '#ffffff';
  const border = 'rgba(148,163,184,0.3)';
  const textPrimary   = '#1e293b';
  const textSecondary = '#64748b';
  const divider       = 'rgba(148,163,184,0.2)';

  return (
    <div style={{
      position: 'fixed',
      left: info.x,
      top: info.y,
      zIndex: 9999,
      pointerEvents: 'none',
      transform: 'translate(-50%, calc(-100% - 10px))',
    }}>
      {/* Arrow */}
      <div style={{
        position: 'absolute',
        bottom: -5,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 10,
        height: 10,
        background: bg,
        border: `1px solid ${border}`,
        borderTop: 'none',
        borderLeft: 'none',
        rotate: '45deg',
        zIndex: -1,
      }} />

      <div style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 10,
        padding: '10px 13px',
        minWidth: 190,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: textPrimary, letterSpacing: '0.02em' }}>
            {symbol}
          </span>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color,
            background: `${color}18`,
            border: `1px solid ${color}40`,
            borderRadius: 4,
            padding: '1px 6px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
            <span style={{ fontSize: 9 }}>{icon}</span>
            {label}
          </span>
        </div>

        {/* Date */}
        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 8 }}>{formattedDay}</div>

        <div style={{ height: 1, background: divider, marginBottom: 8 }} />

        {score !== null ? (
          <>
            {/* Score */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: textSecondary }}>Sentiment Score</span>
              <span style={{
                fontSize: 15,
                fontWeight: 700,
                color,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {score > 0 ? '+' : ''}{score.toFixed(3)}
              </span>
            </div>
            <ScoreBar score={score} />

            <div style={{ height: 1, background: divider, margin: '8px 0' }} />

            {/* Articles */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: textSecondary }}>Articles</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: textPrimary }}>
                {dp.article_count}
              </span>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: textSecondary, textAlign: 'center', padding: '4px 0' }}>
            No data available for this date
          </div>
        )}
      </div>
    </div>
  );
}

export default function SentimentHeatmap({ onSelectAsset }) {
  const [data, setData] = useState([]);
  const [days] = useState(7);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(null);
  const hideTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await getDashboardHeatmap(days, 10);
        if (!cancelled) setData(resp.heatmap || []);
      } catch {}
      setLoading(false);
    }
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [days]);

  const dayLabels = buildDays(days);

  function showTooltip(e, symbol, day, dp) {
    clearTimeout(hideTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      symbol, day, dp,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  }

  function hideTooltip() {
    hideTimer.current = setTimeout(() => setTooltip(null), 80);
  }

  if (loading && data.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center text-slate-500 text-sm">Loading heatmap...</div>;
  }
  if (data.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center text-slate-500 text-sm">Not enough data to render heatmap yet.</div>;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-semibold text-primary text-sm">
          Sentiment Heatmap — Last {days} Days
        </h3>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block bg-red-500/50" /> Bearish
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block bg-slate-300 border border-slate-400" /> Neutral
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block bg-green-500/50" /> Bullish
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0.5 text-xs">
          <thead>
            <tr>
              <th className="text-left px-2 py-1 text-slate-500 font-semibold text-[11px] uppercase tracking-wide">Asset</th>
              {dayLabels.map(d => (
                <th key={d} className="px-1 py-1 text-slate-500 font-semibold text-center text-[10px] whitespace-nowrap">
                  {new Date(d + 'T12:00:00Z').toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(row => {
              const byDay = Object.fromEntries((row.daily || []).map(dp => [dp.day, dp]));
              const validScores = (row.daily || []).map(dp => dp.avg_score).filter(s => s !== null && s !== undefined);
              const aggScore = validScores.length > 0 ? validScores.reduce((a, b) => a + b, 0) / validScores.length : null;
              const aggDp = { avg_score: aggScore, article_count: row.total_articles };
              return (
                <tr key={row.symbol}>
                  <td
                    className="px-2 py-1.5 cursor-pointer hover:bg-slate-200/70 rounded transition-colors"
                    onClick={() => onSelectAsset?.(row.symbol)}
                    onMouseEnter={e => showTooltip(e, row.symbol, null, aggDp)}
                    onMouseLeave={hideTooltip}
                  >
                    <span className="font-bold text-slate-800">{row.symbol}</span>
                    <span className="text-[10px] text-slate-500 ml-1.5">{row.total_articles}</span>
                  </td>
                  {dayLabels.map(d => {
                    const dp = byDay[d];
                    const score = dp?.avg_score ?? null;
                    return (
                      <td
                        key={d}
                        className="text-center rounded cursor-pointer transition-all hover:brightness-125 font-semibold tabular-nums"
                        style={{ ...scoreToStyle(score), padding: '5px 4px', minWidth: 52 }}
                        onMouseEnter={e => showTooltip(e, row.symbol, d, dp || null)}
                        onMouseLeave={hideTooltip}
                        onClick={() => onSelectAsset?.(row.symbol)}
                      >
                        {score !== null ? `${score > 0 ? '+' : ''}${score.toFixed(2)}` : '—'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <HoverTooltip info={tooltip} />
    </div>
  );
}
